import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useParams, useSearchParams } from 'react-router-dom';

import CommentsForm from './CommentsForm';
import TitleBar from '../../../components/TitleBar';

import { AntModal, BorderedSection } from '../../../components';
import { Comment } from '../../../components/assets';
import { Footer } from '../../../components/common/Footer';
import { FIND_SUPPLIER_DEPENDENCIES } from '../queries';

import './supplierDependencies.css';
import { addTypeToElements } from '../../../utils/addTypeToElementOnSupplierMapping';
import FinancialData from '../../../components/common/FinancialData';
import { TableColumnsType } from 'antd';
import { camelCase, startCase } from 'lodash';
import { tableSortingMethods } from '../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';
import {
  UPDATE_DIVISION_LEVEL,
  UPDATE_INTERMEDIATE,
  UPDATE_PERCENTAGE_PRODUCT,
  UPDATE_PERCENTAGE_PRODUCT_GROUP,
} from '../queries/SupplierDependencies';
import DependenciesTitle from '../../../components/common/DependenciesTitle';
import {
  GET_CORPOTATE_CHART,
  GET_LOCATION_CHART,
} from '../../../constants/queryVariables';
import { useUserContext } from '../../../context/PermissionsContext';
import { trimName } from '../../../utils/trimName';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { baseColumnsMapping } from './exportColumns';
import PercentageFormatter from '../../../components/common/PercentageFormatter';
import TitleUpdater from '../../../components/common/TitleUpdater';

const DIVISION_TYPE = 0;
const PRODUCT_GROUP_TYPE = 1;
const PRODUCT_TYPE = 2;
const INTERMEDIATE_TYPE = 3;

const SuppliersMappingPercentages = () => {
  const params = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [itemToUpdate, setitemToUpdate] = useState<any>(null);
  const [isEditing, setisEditing] = useState(false);

  const [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName') || '';

  const { canViewFinancials, canEdit } = useUserContext();

  const { data, loading, error } = useQuery(FIND_SUPPLIER_DEPENDENCIES, {
    variables: { locationUuid: params.locationId! },
  });

  const [updateDivision, { loading: updateDivisionLoading }] = useMutation(
    UPDATE_DIVISION_LEVEL,
    {
      refetchQueries: [
        'FindSupplierDependencies',
        GET_CORPOTATE_CHART,
        GET_LOCATION_CHART,
      ],
    },
  );

  const [updateProductGroup, { loading: updateProductGroupLoading }] =
    useMutation(UPDATE_PERCENTAGE_PRODUCT_GROUP, {
      refetchQueries: [
        'FindSupplierDependencies',
        GET_CORPOTATE_CHART,
        GET_LOCATION_CHART,
      ],
    });

  const [updateProduct, { loading: updateProductLoading }] = useMutation(
    UPDATE_PERCENTAGE_PRODUCT,
    {
      refetchQueries: [
        'FindSupplierDependencies',
        GET_CORPOTATE_CHART,
        GET_LOCATION_CHART,
      ],
    },
  );

  const [updateIntermediate, { loading: updateIntermediateLoading }] =
    useMutation(UPDATE_INTERMEDIATE, {
      refetchQueries: [
        'FindSupplierDependencies',
        GET_CORPOTATE_CHART,
        GET_LOCATION_CHART,
      ],
    });

  const mutations = [
    updateDivision,
    updateProductGroup,
    updateProduct,
    updateIntermediate,
  ];

  const loadingStates =
    updateDivisionLoading ||
    updateProductGroupLoading ||
    updateProductLoading ||
    updateIntermediateLoading;

  const allData = data?.findAll
    ? addTypeToElements(
        data?.findAll?.map((divisionGroup: any) => divisionGroup?.division),
      )
    : [];

  const handleEdit = (record: any) => {
    setisEditing(true);
    setModalOpen(true);
    setitemToUpdate({ ...record, type: getRecordLevel(record.type) });
  };

  const suppliersData = data?.findAll[0]?.division?.suppliers;

  const handleAddComment = () => {
    setisEditing(false);
    setModalOpen(true);
    const { children, ...rest } = allData[0];
    setitemToUpdate(rest);
  };

  const getRecordLevel = (recordType: string) => {
    if (recordType === 'division') {
      return DIVISION_TYPE;
    }

    if (recordType === 'product group') {
      return PRODUCT_GROUP_TYPE;
    }

    if (recordType === 'intermediate') {
      return INTERMEDIATE_TYPE;
    }

    return PRODUCT_TYPE;
  };

  const supplierDependenciesColumns: any = (suppliersData || []).map(
    (supplierDependency: any, index: number) => ({
      title: (
        <DependenciesTitle
          name={
            startCase(
              trimName(supplierDependency.supplierName.toLowerCase()),
            ) || ''
          }
          comment={supplierDependency.comment}
        />
      ),
      dataIndex: 'suppliers',
      key: supplierDependency.key,
      colIndex: index,
      editable: true,
      width: 150,
      align: 'right',
      sorter: canViewFinancials
        ? (a: any, b: any) => tableSortingMethods.number(a.revenue, b.revenue)
        : undefined,
      showSorterTooltip: false,
      render: (_: any, record: any) => {
        const actualSupplier = record?.suppliers?.find(
          (supplier: any) =>
            supplier.supplierName === supplierDependency.supplierName,
        );

        return (
          <div className="custom-table--column">
            <PercentageFormatter value={actualSupplier?.percentage} />
            <FinancialData text={actualSupplier?.revenue} />
          </div>
        );
      },
    }),
  );

  const columns: TableColumnsType<any> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (name: any) => (
        <span className="custom-table--column">{name}</span>
      ),
    },
    {
      title: 'Revenue Supported  Annually',
      dataIndex: 'supportedRevenue',
      key: 'supportedRevenue',
      width: 150,
      fixed: 'left',
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : undefined,
      render: (supportedRevenue: any) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={supportedRevenue} />}
        </span>
      ),
    },
    ...supplierDependenciesColumns,
  ];

  const formFields = suppliersData?.map((supplier: any) => {
    return {
      label: supplier.supplierName,
      key: camelCase(supplier.supplierName),
      type: 'percentage',
      defaultValue: supplier.percentage,
    };
  });

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    const { type } = currentRecord;

    const mutation = mutations[getRecordLevel(type)];

    return {
      mutation,
    };
  };

  const mappingColumns: ExportColumn[] = [];

  if (suppliersData) {
    suppliersData.forEach((supplier: any, index: number) => {
      mappingColumns.push({
        key: camelCase(supplier.supplierName),
        header: supplier.supplierName,
        type: 'percentage',
        nestedKey: `suppliers.${index}.percentage`,
        width: 35,
      });
    });
  }

  return (
    <>
      <TitleUpdater title={`${locationName}/Supplier Mapping`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={` ${locationName} // Suppliers`}
        brief={`In the matrix, enter the degree to which each division, product group, product, or intermediate depends on the Supplier to the ${locationName} location.`}
      />
      <QueryErrorBoundary error={error}>
        <BorderedSection
          title="Supplier Dependencies"
          noPadding
          extendOptions={
            canEdit ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddComment}
                  className="text-blue underline hover:cursor-pointer font-normal"
                >
                  Add / Edit Commentary
                </button>
                <Comment />
              </div>
            ) : null
          }
        >
          <TableV3
            title="Supplier Dependencies"
            columns={columns as unknown as ColumnsType<any>}
            data={allData}
            loading={loading || loadingStates}
            editable
            formFields={formFields}
            modifyVariables={modifyVariables}
            customStyles="custom-table--dependency-mapping nested-columns--custom"
            inlineEditing
            useBorderedSection={false}
            editableOptions={{
              create: false,
              update: true,
              delete: false,
            }}
            isMatrix
            matrixType="suppliers"
            scroll={{ x: 800, y: 340 }}
            exportExcelColumns={[...baseColumnsMapping, ...mappingColumns]}
          />
        </BorderedSection>
      </QueryErrorBoundary>
      <AntModal
        openModal={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          isEditing
            ? 'Supplier Dependencies'
            : 'Supplier Dependencies Commentary'
        }
      >
        <CommentsForm
          data={itemToUpdate}
          onCancel={() => setModalOpen(false)}
          isEditing={isEditing}
        />
      </AntModal>
      <Footer
        backPath={`/suppliers-dependencies/${params.locationId}?locationName=${locationName}`}
        nextPath={`/suppliers-dependencies/${params.locationId}/summary?locationName=${locationName}`}
      />
    </>
  );
};

export default SuppliersMappingPercentages;
